import { useEffect, useRef } from 'react';

export interface ProjectUpdateMessage {
  type: 'project_updated' | 'agent_status' | 'ready' | 'error';
  projectId?: number;
  cardId?: number | null;
  updateType?: string;
  agentId?: number;
  agentName?: string;
  text?: string;
  /** For updateType 'database_file_changed': the databases/-relative path. */
  path?: string;
  /** For updateType 'database_file_changed': database | chart | dashboard | notes. */
  fileKind?: string;
  /**
   * For updateType 'card_moved' / 'card_moved_project': who moved the card,
   * so the receiving board can animate a ghost-cursor drag without a second
   * lookup. actorName is '' when the server could not resolve it.
   */
  actorName?: string;
  actorUserId?: number | null;
  actorAgentId?: number | null;
  /** Destination hints for a move (geometry is still derived from the refetch). */
  toColumnId?: number | null;
  toEpicId?: number | null;
  toSprintId?: number | null;
  /** For a move into a celebration column: the shared animation style so
   *  every client plays the same one. Null/absent for non-celebration moves. */
  celebrationStyle?: string | null;
  /** Work stream nudge payload for updateType 'work_stream_updated'. */
  workStreamId?: number;
  cursor?: number;
}

const RECONNECT_DELAY_MS = 4_000;
const KEEPALIVE_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Live per-project updates: the server sends lightweight
 * "project_updated" nudges (refetch now) whenever anything on the
 * project changes — including changes made by agent jobs in other
 * processes — plus transient agent_status lines for cards being worked.
 * Polling stays as the fallback for missed events.
 */
export function useProjectSocket(
  projectId: number,
  onMessage: (message: ProjectUpdateMessage) => void,
  workStreamId?: number,
): void {
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!projectId) return;
    let socket: WebSocket | null = null;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let keepaliveTimer: ReturnType<typeof setInterval> | undefined;
    let recovering = false;
    let reconnectAttempts = 0;

    const connect = () => {
      if (closed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = new URL(
        `${protocol}//${window.location.host}/api/ws/projects/${projectId}`,
      );
      if (
        recovering &&
        Number.isSafeInteger(workStreamId) &&
        Number(workStreamId) > 0
      ) {
        url.searchParams.set('recovered', '1');
        url.searchParams.set('workStreamId', String(workStreamId));
      }
      socket = new WebSocket(url);

      socket.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const message = JSON.parse(event.data) as ProjectUpdateMessage;
          if (message.type === 'ready') reconnectAttempts = 0;
          handlerRef.current(message);
        } catch {
          // ignore malformed frames
        }
      };
      socket.onclose = () => {
        if (closed) return;
        recovering = true;
        reconnectAttempts += 1;
        if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) return;
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      };
      socket.onerror = () => {
        socket?.close();
      };
    };

    connect();
    keepaliveTimer = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send('ping');
      }
    }, KEEPALIVE_MS);

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (keepaliveTimer) clearInterval(keepaliveTimer);
      socket?.close();
    };
  }, [projectId, workStreamId]);
}
